import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import NotificationBell from './NotificationBell';

/**
 * AppNavBar — shared persistent top nav for all authenticated pages.
 * Fixed at top (h-16). AuthLayout adds pt-16 to the page body.
 */
const AppNavBar = () => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close drawer on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    setDrawerOpen(false);
    navigate('/');
  };

  const navTo = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  // Role-based nav links
  const navLinks = [];
  if (user?.role === 'client') {
    navLinks.push({ label: t('nav.home'), path: '/dashboard' });
    navLinks.push({ label: t('nav.browse'), path: '/catalog' });
    navLinks.push({ label: t('nav.my_requests'), path: '/dashboard' });
  } else if (user?.role === 'provider') {
    navLinks.push({ label: t('nav.my_jobs'), path: '/dashboard' });
    navLinks.push({ label: t('nav.my_profile'), path: user?.id ? `/provider/${user.id}` : '/provider-profile' });
    navLinks.push({ label: t('nav.verification'), path: '/provider/verification' });
  } else if (user?.role === 'admin') {
    navLinks.push({ label: t('nav.home'), path: '/dashboard' });
    navLinks.push({ label: t('nav.management'), path: '/admin-management' });
  }

  // Initials from name
  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const roleLabel = user?.role === 'client' ? 'Cliente'
    : user?.role === 'provider' ? 'Proveedor'
    : user?.role === 'admin' ? 'Admin'
    : '';

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-40 h-16 bg-white border-b flex items-center px-4">
        <div className="container mx-auto flex items-center justify-between gap-4">
          {/* Left — Logo */}
          <button
            onClick={() => navTo('/dashboard')}
            className="text-xl font-bold flex-shrink-0"
            style={{ fontFamily: 'Outfit', color: 'hsl(215 100% 34%)' }}
          >
            Servicios
          </button>

          {/* Center — Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Button key={link.path + link.label} variant="ghost" size="sm" onClick={() => navTo(link.path)}>
                {link.label}
              </Button>
            ))}
          </div>

          {/* Right — Language + Bell + User */}
          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="hidden sm:flex gap-0.5">
              {['es', 'en', 'fr'].map(lang => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                    i18n.language === lang
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Notification bell */}
            {user && <NotificationBell />}

            {/* User avatar + dropdown */}
            {user && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(o => !o)}
                  className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold hover:opacity-90 transition-opacity"
                  aria-label="User menu"
                >
                  {initials}
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-10 w-48 bg-white rounded-lg border shadow-lg py-1 z-50">
                    <div className="px-3 py-2 border-b">
                      <p className="text-sm font-medium truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{roleLabel}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      data-testid="logout-button"
                    >
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-1.5 rounded hover:bg-gray-100 transition-colors"
              onClick={() => setDrawerOpen(o => !o)}
              aria-label={t('nav.menu')}
            >
              {drawerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed top-16 right-0 bottom-0 z-40 w-64 bg-white border-l shadow-xl transform transition-transform duration-200 ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full p-4 gap-1">
          {/* Nav links */}
          {navLinks.map(link => (
            <button
              key={link.path + link.label}
              onClick={() => navTo(link.path)}
              className="text-left px-3 py-2.5 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              {link.label}
            </button>
          ))}

          <div className="flex-1" />

          {/* Language switcher in drawer */}
          <div className="flex gap-1 px-3 py-2">
            {['es', 'en', 'fr'].map(lang => (
              <button
                key={lang}
                onClick={() => changeLanguage(lang)}
                className={`flex-1 text-xs py-1 rounded font-medium ${
                  i18n.language === lang
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-gray-100'
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

          {/* User info + logout in drawer */}
          {user && (
            <div className="border-t pt-3 mt-1">
              <div className="px-3 py-1 mb-1">
                <p className="text-sm font-medium">{user.name}</p>
                <p className="text-xs text-muted-foreground">{roleLabel}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full text-left px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                {t('nav.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AppNavBar;
